import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Paper,
  Grid,
  Divider,
  Collapse,
  Fade,
} from "@mui/material";
import {
  Close,
  LocalShipping,
  Person,
  Receipt,
  Timeline,
  ExpandMore,
  ExpandLess,
  CalendarToday,
  ShoppingBag,
  LocationOn,
  Inventory,
  Cancel,
  ErrorOutline,
  FileCopy,
  AssignmentReturn,
  Description,
  Warning,
} from "@mui/icons-material";

const StatusTimeline = ({ status }) => {
  const steps = [
    { label: "Yeni", icon: Receipt, description: "Sipariş alındı" },
    {
      label: "Hazırlanıyor",
      icon: ShoppingBag,
      description: "Sipariş hazırlanıyor",
    },
    {
      label: "Kargoya Verildi",
      icon: LocalShipping,
      description: "Sipariş kargoya verildi",
    },
    { label: "Tamamlandı", icon: Timeline, description: "Sipariş tamamlandı" },
  ];

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
        px: { xs: 1, sm: 2 },
        bgcolor: "white",
        borderRadius: { xs: "12px", sm: "16px" },
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      {/* Desktop Timeline */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 2,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 2,
            bgcolor: "grey.200",
            transform: "translateY(-50%)",
            zIndex: 0,
          },
        }}
      >
        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: index <= status ? "primary.main" : "white",
                border: "2px solid",
                borderColor: index <= status ? "primary.main" : "grey.300",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
                transition: "all 0.3s ease",
                boxShadow:
                  index <= status ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
                transform: index <= status ? "scale(1.1)" : "scale(1)",
              }}
            >
              {React.createElement(step.icon, {
                sx: {
                  color: index <= status ? "white" : "grey.400",
                  fontSize: "1.25rem",
                },
              })}
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: index <= status ? "primary.main" : "text.secondary",
                fontWeight: index <= status ? 600 : 400,
                textAlign: "center",
                mb: 0.5,
              }}
            >
              {step.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                display: "block",
                maxWidth: 120,
                opacity: index <= status ? 1 : 0.7,
              }}
            >
              {step.description}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Mobile Timeline */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: "12px",
              bgcolor: index <= status ? "primary.light" : "grey.50",
              border: "1px solid",
              borderColor: index <= status ? "primary.main" : "grey.200",
              transition: "all 0.3s ease",
              opacity: index <= status ? 1 : 0.7,
              transform: index <= status ? "translateX(4px)" : "none",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                minWidth: 36,
                bgcolor: index <= status ? "primary.main" : "white",
                border: "2px solid",
                borderColor: index <= status ? "primary.main" : "grey.300",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  index <= status ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {React.createElement(step.icon, {
                sx: {
                  color: index <= status ? "white" : "grey.400",
                  fontSize: "1.1rem",
                },
              })}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: index <= status ? "primary.main" : "text.primary",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  mb: 0.25,
                }}
              >
                {step.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.75rem",
                }}
              >
                {step.description}
              </Typography>
            </Box>
            {index <= status && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const StatusError = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 4: // CancellationReturnExchangeRequest
        return {
          label: "İade & Değişim Talebi",
          icon: AssignmentReturn,
          color: "warning",
          severity: "warning",
          message: "Bu sipariş için iade veya değişim talebi oluşturulmuş.",
          bgColor: "#fff3cd",
          borderColor: "#ffc107",
        };
      case 5: // Cancelled
        return {
          label: "İptal Edilen",
          icon: Cancel,
          color: "error",
          severity: "error",
          message: "Bu sipariş iptal edilmiştir.",
          bgColor: "#f8d7da",
          borderColor: "#dc3545",
        };
      case 6: // Undeliverable
        return {
          label: "Teslim Edilmeyen",
          icon: ErrorOutline,
          color: "error",
          severity: "error",
          message: "Bu sipariş teslim edilememiştir.",
          bgColor: "#f8d7da",
          borderColor: "#dc3545",
        };
      case 7: // Invoiced
        return {
          label: "Faturalandırılmış",
          icon: Description,
          color: "info",
          severity: "info",
          message: "Bu sipariş faturalandırılmıştır.",
          bgColor: "#d1ecf1",
          borderColor: "#17a2b8",
        };
      default:
        return {
          label: "Bilinmeyen Durum",
          icon: Error,
          color: "default",
          severity: "info",
          message: "Sipariş durumu belirlenemiyor.",
          bgColor: "#f8f9fa",
          borderColor: "#6c757d",
        };
    }
  };
  const config = getStatusConfig(status);

  return (
    <Box sx={{ py: 3 }}>
      {/* Ana Status Kutusu */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          p: 3,
          borderRadius: 2,
          border: "2px solid",
          borderColor: config.borderColor,
          bgcolor: config.bgColor,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            bgcolor: config.borderColor,
            borderRadius: "50%",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <config.icon sx={{ color: "white", fontSize: "2rem" }} />
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: config.borderColor,
            textAlign: "center",
          }}
        >
          {config.label}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          {config.message}
        </Typography>
      </Box>
    </Box>
  );
};

const InfoCard = ({ icon: Icon, title, value, highlight }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      height: "100%",
      borderRadius: { xs: "12px", sm: "16px" },
      background: highlight ? "primary.main" : "white",
      border: "1px solid",
      borderColor: highlight ? "primary.main" : "grey.200",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: { xs: "none", sm: "translateY(-4px)" },
        boxShadow: { xs: "none", sm: "0 8px 16px rgba(0,0,0,0.08)" },
        borderColor: "primary.main",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "row", sm: "column" },
        height: "100%",
        gap: { xs: 1, sm: 1.5 },
        alignItems: { xs: "center", sm: "flex-start" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          flex: { xs: "none", sm: "auto" },
        }}
      >
        <Box
          sx={{
            bgcolor: highlight ? "rgba(255,255,255,0.2)" : "grey.50",
            borderRadius: { xs: "8px", sm: "12px" },
            p: { xs: 0.75, sm: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: { xs: 32, sm: "auto" },
          }}
        >
          <Icon
            sx={{
              color: highlight ? "white" : "primary.main",
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: highlight ? "white" : "text.secondary",
            fontWeight: 500,
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            display: { xs: "none", sm: "block" },
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: highlight ? "white" : "text.secondary",
            display: { xs: "block", sm: "none" },
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: highlight ? "white" : "text.primary",
            fontSize: { xs: "0.875rem", sm: "1rem" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

export const OrdersPopUpModal = ({
  openDialog,
  handleCloseDialog,
  selectedItem,
}) => {
  const [expandedSection, setExpandedSection] = useState("products");
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? "" : section);
  };

  if (!selectedItem) return null;

  const ProductCard = ({ product }) => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: { xs: "12px", sm: "16px" },
        border: "1px solid",
        borderColor: "grey.200",
        bgcolor: "white",
      }}
    >
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              sx={{
                bgcolor: "grey.50",
                borderRadius: { xs: "8px", sm: "12px" },
                p: { xs: 0.75, sm: 1 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Inventory
                sx={{
                  color: "primary.main",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {product.productName}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: { xs: 0.5, sm: 1 },
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                <Chip
                  label={`Barkod: ${product.productBarcode}`}
                  size="small"
                  sx={{
                    bgcolor: "grey.100",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: 20, sm: 24 },
                  }}
                />
                <Chip
                  label={`Adet: ${product.quantity}`}
                  size="small"
                  sx={{
                    bgcolor: "grey.100",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: 20, sm: 24 },
                  }}
                />
                <Chip
                  label={`KDV: %${product.vatRate}`}
                  size="small"
                  sx={{
                    bgcolor: "grey.100",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: 20, sm: 24 },
                  }}
                />
              </Box>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Fiyat
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {product.amount} {product.currencyCode}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    İndirim
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="error.main"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    -{product.discountPrice} {product.currencyCode}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Net Fiyat
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary.main"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {product.price} {product.currencyCode}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const Section = ({ title, content, id }) => (
    <Paper
      sx={{
        mb: { xs: 2, sm: 2.5 },
        overflow: "hidden",
        borderRadius: { xs: "12px", sm: "16px" },
        bgcolor: "white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Box
        onClick={() => toggleSection(id)}
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          bgcolor: expandedSection === id ? "primary.main" : "white",
          transition: "all 0.2s ease",
          "&:hover": {
            bgcolor: expandedSection === id ? "primary.dark" : "grey.50",
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: expandedSection === id ? "white" : "text.primary",
            fontWeight: 600,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          {title}
        </Typography>
        {expandedSection === id ? (
          <ExpandLess
            sx={{ color: "white", fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
          />
        ) : (
          <ExpandMore
            sx={{
              color: "text.secondary",
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          />
        )}
      </Box>
      <Collapse in={expandedSection === id}>
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            bgcolor: "grey.50",
            borderTop: "1px solid",
            borderColor: "grey.200",
          }}
        >
          {content}
        </Box>
      </Collapse>
    </Paper>
  );

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          m: { xs: 0, sm: 2 },
          height: { xs: "100%", sm: "auto" },
          maxHeight: { xs: "100%", sm: "90vh" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: { xs: 2, sm: 2.5 },
          borderRadius: { xs: 0, sm: "16px 16px 0 0" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
        >
          Sipariş #{selectedItem.orderNumber}
        </Typography>
        <Chip
          label={`${selectedItem.totalPrice} ${selectedItem.currencyCode}`}
          sx={{
            bgcolor: "white",
            color: "primary.main",
            fontWeight: 600,
            px: { xs: 1, sm: 2 },
            height: { xs: 28, sm: 32 },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            mr: { xs: 0, sm: 4 },
            width: { xs: "100%", sm: "auto" },
          }}
        />
        <IconButton
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: { xs: 4, sm: 8 },
            top: { xs: 4, sm: 8 },
            p: { xs: 0.5, sm: 1 },
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Close sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          bgcolor: "grey.50",
          height: { xs: "calc(100% - 80px)", sm: "auto" },
          overflow: "auto",
        }}
      >
        {selectedItem.status > 3 ? (
          <StatusError status={selectedItem.status} />
        ) : (
          <StatusTimeline status={selectedItem.status} />
        )}

        <Grid
          container
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              icon={CalendarToday}
              title="Sipariş Tarihi"
              value={new Date(selectedItem.orderDate).toLocaleDateString(
                "tr-TR"
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              icon={LocalShipping}
              title="Kargo Firması"
              value={selectedItem.cargoProviderName}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              icon={Receipt}
              title="Takip Numarası"
              value={selectedItem.cargoTrackingNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              icon={ShoppingBag}
              title="Toplam Tutar"
              value={`${selectedItem.totalPrice} ${selectedItem.currencyCode}`}
            />
          </Grid>
        </Grid>
        <Section
          id="products"
          title="Ürün Bilgileri"
          content={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              {selectedItem.orderLines.map((product, index) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </Box>
          }
        />
        <Section
          id="customer"
          title="Müşteri Bilgileri"
          content={
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={Person}
                  title="Müşteri"
                  value={`${selectedItem.customerFirstName} ${selectedItem.customerLastName}`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={LocationOn}
                  title="Email"
                  value={selectedItem.customerEmail}
                />
              </Grid>
            </Grid>
          }
        />

        <Section
          id="payment"
          title="Ödeme Detayları"
          content={
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <InfoCard
                  icon={Receipt}
                  title="Brüt Tutar"
                  value={`${selectedItem.grossAmount} ${selectedItem.currencyCode}`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoCard
                  icon={Receipt}
                  title="İndirim"
                  value={`${selectedItem.totalDiscount} ${selectedItem.currencyCode}`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoCard
                  icon={Receipt}
                  title="Net Tutar"
                  value={`${selectedItem.totalPrice} ${selectedItem.currencyCode}`}
                />
              </Grid>
            </Grid>
          }
        />
      </DialogContent>
    </Dialog>
  );
};

export default OrdersPopUpModal;
